import { useState } from 'react'
import { Cookie } from 'lucide-react'

const CONSENT_FLAG = 'cookie-consent-v1'

/**
 * First-visit cookie notice.
 *
 * The app sets exactly two first-party httpOnly cookies, both for session
 * authentication only (see docs/en/11-cookie-policy.md). There is nothing to
 * "consent to" beyond knowing they exist — so the banner just says so, links
 * to the policies, and stores a single localStorage flag so it never returns.
 * No cookie is created by this component; no tracking of any kind.
 */
export function CookieConsent() {
  // Decide visibility once, up front — and survive storage being blocked
  // (private window etc.): if we can't read the flag, show the banner but
  // degrade gracefully on OK (it just comes back next visit).
  const [visible, setVisible] = useState<boolean>(() => {
    try {
      return !localStorage.getItem(CONSENT_FLAG)
    } catch {
      return true
    }
  })

  if (!visible) return null

  const accept = () => {
    try {
      localStorage.setItem(CONSENT_FLAG, new Date().toISOString())
    } catch {
      // storage blocked — banner reappears next visit; nothing is lost
    }
    setVisible(false)
  }

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-4">
      <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-50 flex-shrink-0">
            <Cookie className="w-5 h-5 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">Cookies — the short version</p>
            <p className="text-sm text-gray-600 mt-1">
              We set two first-party cookies, <strong>only</strong> to keep you signed in.
              No tracking, no analytics, no third parties.
              See our <a href="/privacy" className="text-blue-600 hover:underline">privacy policy</a>
              {' '}and <a href="/cookies" className="text-blue-600 hover:underline">cookie policy</a>.
            </p>
            <button
              onClick={accept}
              className="mt-3 px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
